import { recallStore } from '../recall-campaigns/store';
import { effectiveLanguage, isRtl, languageName, reviewedLine, textCountsByLanguage } from '../platform-languages';
import type { LanguageCode } from '../platform-languages';
import type { RecallGoal } from '../settings-recall-agent/types';
import {
  FIELD_BY_KEY,
  RECALL_FIELDS,
  fieldUnavailableReason,
} from './types';
import type {
  AudiencePatient,
  BuilderStep,
  BuilderToken,
  FieldKey,
  RecallDraft,
  StepChannel,
  StepIssue,
  StepMode,
  Suggestion,
} from './types';

/** Added to every outbound text for the patient, and it counts (§3.5). */
export const OPT_OUT_LINE = 'Reply STOP to opt out';

/** Plain words, with each field at its longest likely value. */
export function tokenPlain(tokens: BuilderToken[], values?: Partial<Record<FieldKey, string>>): string {
  return tokens
    .map((t) => {
      if (t.kind === 'text') return t.text;
      if (t.kind === 'unknown') return t.raw;
      return values?.[t.field] ?? FIELD_BY_KEY[t.field].example;
    })
    .join('');
}

/**
 * Resolve a message for one patient, reporting **what each field became** —
 * including the fallback where one was used, which is what makes the preview
 * honest rather than decorative.
 */
export function resolveForPatient(
  tokens: BuilderToken[],
  patient: AudiencePatient,
  fallbacks: Partial<Record<FieldKey, string>> | undefined,
): { text: string; used: { field: FieldKey; label: string; value: string; wasFallback: boolean }[] } {
  const used: { field: FieldKey; label: string; value: string; wasFallback: boolean }[] = [];
  const text = tokens
    .map((t) => {
      if (t.kind === 'text') return t.text;
      if (t.kind === 'unknown') return t.raw;
      const real = patient.values[t.field];
      const fallback = fallbacks?.[t.field] ?? FIELD_BY_KEY[t.field].defaultFallback;
      const value = real ?? fallback ?? FIELD_BY_KEY[t.field].example;
      used.push({
        field: t.field,
        label: FIELD_BY_KEY[t.field].label,
        value,
        wasFallback: real === undefined,
      });
      return value;
    })
    .join('');
  return { text, used };
}

/** 160 characters a segment, and **the opt-out line counts** — it is going out. */
export function textCounts(tokens: BuilderToken[]): { chars: number; segments: number } {
  const chars = tokenPlain(tokens).length + OPT_OUT_LINE.length + 1;
  return { chars, segments: Math.max(1, Math.ceil(chars / 160)) };
}

/**
 * The same count, **per language** (amendment 25 §7). A single segment count is
 * wrong for one of the languages the practice sends in: Farsi fits 70 characters
 * a part, not 160, so a message that is one text in English is three in Farsi.
 */
export function textCountsPerLanguage(tokens: BuilderToken[]) {
  return textCountsByLanguage(tokenPlain(tokens), OPT_OUT_LINE);
}

/** Over two messages warns; over four blocks (§3.5). */
export function segmentSeverity(segments: number): 'ok' | 'warn' | 'block' {
  if (segments > 4) return 'block';
  if (segments > 2) return 'warn';
  return 'ok';
}

/** Every field a message actually uses, in order, without duplicates. */
export function fieldsUsed(step: BuilderStep): FieldKey[] {
  const all = [...(step.subject ?? []), ...(step.body ?? []), ...(step.backup ?? [])];
  const out: FieldKey[] = [];
  for (const t of all) {
    if (t.kind === 'field' && !out.includes(t.field)) out.push(t.field);
  }
  for (const f of step.mustInclude ?? []) {
    if (!out.includes(f)) out.push(f);
  }
  return out;
}

function hasUnknown(step: BuilderStep): string | undefined {
  const all = [...(step.subject ?? []), ...(step.body ?? []), ...(step.backup ?? [])];
  return all.find((t) => t.kind === 'unknown')?.raw;
}

function isEmpty(tokens: BuilderToken[] | undefined): boolean {
  return tokens === undefined || tokenPlain(tokens).trim() === '';
}

/**
 * What is incomplete, as links to the step (§3.7). **Not a disabled button with
 * no explanation** — the page says exactly what is missing.
 */
export function stepIssues(draft: RecallDraft): StepIssue[] {
  const issues: StepIssue[] = [];
  draft.steps.forEach((step, i) => {
    const add = (short: string, long: string) => {
      issues.push({ stepId: step.id, stepIndex: i + 1, short, long });
    };

    const unknown = hasUnknown(step);
    if (unknown !== undefined) add(`Unknown field ${unknown}`, `Step ${String(i + 1)} has an unknown field ${unknown}`);

    if (step.channel === 'human_call') return;

    if (step.mode === 'smart') {
      if ((step.brief ?? '').trim() === '') add('Needs a brief', `Step ${String(i + 1)} needs a brief`);
      // The backup is required in Smart mode — no save without one.
      if (isEmpty(step.backup)) add('Needs a backup', `Step ${String(i + 1)} needs a backup message`);
    } else {
      if (isEmpty(step.body)) add('Message is empty', `Step ${String(i + 1)}’s message is empty`);
      if (step.channel === 'email' && isEmpty(step.subject)) {
        add('Needs a subject', `Step ${String(i + 1)} needs a subject line`);
      }
    }

    // A field some patients lack, with no fallback, is an issue (§3.5).
    for (const key of fieldsUsed(step)) {
      const field = FIELD_BY_KEY[key];
      if (!field.mayBeEmpty) continue;
      const fallback = step.fallbacks?.[key] ?? field.defaultFallback;
      if (fallback === undefined || fallback.trim() === '') {
        add(`${field.label} needs a fallback`, `Step ${String(i + 1)}: ${field.label} needs a fallback`);
      }
    }

    if (step.channel === 'text') {
      const tokens = step.mode === 'smart' ? (step.backup ?? []) : (step.body ?? []);
      if (segmentSeverity(textCounts(tokens).segments) === 'block') {
        add('Too long', `Step ${String(i + 1)} is longer than 4 text messages`);
      }
    }
  });
  return issues;
}

/**
 * Start needs a goal, criteria, a name, at least one step, every step complete,
 * and at least one patient who can be contacted (backend §5.2).
 */
export function canStart(draft: RecallDraft): { ok: boolean; blockers: string[] } {
  const blockers: string[] = [];
  if (draft.goal === undefined) blockers.push('Pick what the recall is for');
  if (draft.readback === undefined) blockers.push('Describe who should get it and build the list');
  if (draft.name.trim() === '') blockers.push('Give the recall a name');
  if (draft.steps.length === 0) blockers.push('Add at least one step');
  if ((draft.count?.contactable ?? 0) === 0) blockers.push('Nobody in this audience can be contacted');
  return { ok: blockers.length === 0 && stepIssues(draft).length === 0, blockers };
}

/** Continue from page 1 needs a goal and a build that found somebody. */
export function canContinueFromAudience(draft: RecallDraft): boolean {
  if (draft.goal === undefined || draft.readback === undefined) return false;
  if ((draft.count?.match ?? 0) === 0) return false;
  // A clinical safety recall needs a clinical reason, checked at this gate.
  if (draft.goal === 'clinical_safety' && !draft.readback.hasClinicalCriterion) return false;
  return true;
}

export const CLINICAL_ERROR =
  'A clinical safety recall needs a clinical reason. Describe it — for example, perio patients overdue for maintenance.';

/**
 * Which channels a goal allows. **Loyalty is text or email only, one step** —
 * a greeting placed as a call is the wrong pressure.
 */
export function channelsFor(goal: RecallGoal): StepChannel[] {
  return goal === 'increase_loyalty' ? ['text', 'email'] : ['text', 'email', 'ai_call', 'human_call'];
}

/** An AI call is Smart only: a template read aloud is a robocall. */
export function modesFor(channel: StepChannel): StepMode[] {
  if (channel === 'ai_call') return ['smart'];
  if (channel === 'human_call') return [];
  return ['template', 'smart'];
}

/** Loyalty is one step; otherwise at most eight (backend §5.1). */
export function maxSteps(goal: RecallGoal | undefined): number {
  return goal === 'increase_loyalty' ? 1 : 8;
}

/**
 * Days must stay in order. A step dropped earlier takes a day between its
 * neighbours; **where there is no room it takes the neighbour's day + 1 and the
 * ones after shift by one**, and the caller says so (§3.3).
 */
export function reorderSteps(steps: BuilderStep[], from: number, to: number): { steps: BuilderStep[]; shifted: boolean } {
  const next = [...steps];
  const moved = next.splice(from, 1)[0];
  if (moved === undefined) return { steps, shifted: false };
  next.splice(to, 0, moved);
  const out = next.map((s) => ({ ...s }));

  // The moved step takes a day BETWEEN its new neighbours — keeping its old day
  // when it has been dragged to the front would push every other step out, which
  // is the opposite of what dropping it there meant.
  const lower = to > 0 ? (out[to - 1]?.day ?? -1) : -1;
  const upper = out[to + 1]?.day;
  const target = out[to];
  if (target !== undefined) {
    target.day = upper !== undefined && upper - lower >= 2 ? lower + 1 : Math.max(0, lower + 1);
  }

  // Then days must still ascend. Where there was no room, the ones after shift by
  // a day and the caller says so.
  let shifted = false;
  for (let i = 1; i < out.length; i++) {
    const prev = out[i - 1];
    const cur = out[i];
    if (prev === undefined || cur === undefined) continue;
    if (cur.day <= prev.day) {
      cur.day = prev.day + 1;
      shifted = true;
    }
  }
  return { steps: out, shifted };
}

/** The gap rendered between two step cards. */
export function gapLabel(a: BuilderStep, b: BuilderStep): string {
  const n = b.day - a.day;
  return `+${String(n)} ${n === 1 ? 'day' : 'days'}`;
}

/** The three patients the previews use — real ones from the audience. */
export function previewPatients(draft: RecallDraft): AudiencePatient[] {
  return draft.patients.filter((p) => !draft.leftOut.includes(p.id)).slice(0, 3);
}

/** Fields offerable right now, each with the reason when it is not. */
export function fieldMenu(ctx: {
  goal: RecallGoal;
  mode: StepMode;
  hasDueCriterion: boolean;
  mentionBenefits: boolean;
  /** A call cannot carry a link (§2.8). */
  channel?: StepChannel;
}) {
  return RECALL_FIELDS.map((f) => {
    const spokenBlock =
      ctx.channel === 'ai_call' && f.key === 'booking_link'
        ? 'Not on a call — there is nothing to tap'
        : undefined;
    return { field: f, unavailable: spokenBlock ?? fieldUnavailableReason(f, ctx) };
  });
}

/**
 * Where **Continue** on a draft should land: the first page that has something to
 * fix (amendment 22 §4). Dropping somebody on page 1 when the audience is already
 * built makes them click through work they have finished.
 */
export function firstUnfinishedPage(draft: RecallDraft): 'audience' | 'messages' | 'test' {
  if (draft.goal === undefined || draft.readback === undefined || (draft.count?.match ?? 0) === 0) return 'audience';
  if (draft.steps.length === 0 || stepIssues(draft).length > 0) return 'messages';
  if (draft.name.trim() === '') return 'messages';
  return 'test';
}

/**
 * How many patients this recall would reach **right now** — the audience count
 * less anyone left out by hand (spec 23 §1.3).
 *
 * Pages 2 and 3 were reading `count.contactable` straight, so *Who: 387 patients*
 * never moved when somebody was left out on page 1. One helper, three pages.
 */
export function contactableNow(draft: RecallDraft): number {
  return Math.max(0, (draft.count?.contactable ?? 0) - draft.leftOut.length);
}

export function matchingNow(draft: RecallDraft): number {
  return Math.max(0, (draft.count?.match ?? 0) - draft.leftOut.length);
}

/**
 * Whether the steps are still the ones a goal loaded, untouched.
 *
 * Changing the goal replaces the starting steps — but **steps somebody has
 * already edited are kept** (spec 21 §3.2), so this is the test for "nothing has
 * been written yet".
 */
export function stepsAreUntouched(steps: BuilderStep[], goal: RecallGoal | undefined): boolean {
  if (steps.length === 0) return true;
  if (goal === undefined) return false;
  return steps.every(
    (s) =>
      (s.brief ?? '') === '' &&
      (s.body ?? []).length === 0 &&
      (s.subject ?? []).length === 0 &&
      (s.backup ?? []).length === 0 &&
      (s.callerNote ?? '') === '',
  );
}

/**
 * Keep the list in day order after a day is typed (spec 23 §2.5).
 *
 * Setting a step to day 3 when it sits after a day-21 step used to leave it last
 * and render the gap as `+-18 days`. Re-sorting is the kinder of the two options
 * the fix list offers — refusing the number makes somebody reorder by hand for a
 * change the page could simply absorb.
 */
export function sortStepsByDay(steps: BuilderStep[]): BuilderStep[] {
  return [...steps].sort((a, b) => a.day - b.day);
}

/**
 * Which fields a channel can carry (spec 23 §2.8). **A call has no booking link**
 * — there is nothing to tap in a phone call, and offering it in *It must include*
 * promises ELVA will say a URL out loud.
 */
export function fieldsForChannel(channel: StepChannel): (key: FieldKey) => boolean {
  const spoken = channel === 'ai_call';
  return (key) => !(spoken && key === 'booking_link');
}

/**
 * *Not in a recall yet*, computed (fix 23 §5.2).
 *
 * Only **active** recalls cover anybody — a paused one is holding its patients,
 * not reaching them. Clamped at zero so a mock number that drifts under the store
 * shows as none left rather than as a negative.
 */
export function uncoveredFor(suggestion: Suggestion): number {
  const ids = suggestion.coveredBy ?? [];
  const reached = recallStore.rows
    .filter((r) => r.state === 'active' && ids.includes(r.id))
    .reduce((n, r) => n + (r.inProgress ?? 0), 0);
  return Math.max(0, suggestion.matches - reached);
}

/** The language a recall preview should render in (§7). */
export function recallPreviewLanguage(patient: AudiencePatient, showEnglish: boolean): LanguageCode {
  return showEnglish ? 'en' : effectiveLanguage(patient.language ?? 'en');
}

/** Right to left, for a Farsi preview. */
export function recallPreviewRtl(patient: AudiencePatient, showEnglish: boolean): boolean {
  return isRtl(recallPreviewLanguage(patient, showEnglish));
}

/** The opt-out line, in the language it will actually go in. */
export function recallOptOut(patient: AudiencePatient, showEnglish: boolean): string {
  return reviewedLine('opt_out', recallPreviewLanguage(patient, showEnglish));
}

/** `Farsi` — for the preview-as chip's second line. */
export function recallLanguageLabel(patient: AudiencePatient): string {
  return languageName(patient.language ?? 'en');
}
